/**
 * AdminPeerHandler - Implements PeerTypeHandler for admin session peers.
 *
 * Replaces the legacy AdminSessionManager. Manages admin session lifecycle:
 * input filter toggling, elevated PowerShell launch, idle timer with
 * warning/kick, session end notification, and reboot scheduling.
 * Chunked elevated PowerShell script delivery is delegated to AdminPowerShellScriptManager.
 *
 * Registered with PeerTypeRegistry for the 'admin' peer type.
 * Called by InputManager for admin control messages (adminSessionExit).
 * Called by realtime WebSocket handler for ADMIN_SESSION_END messages.
 */

import type { PeerMetadata } from '@playcastdotio/Messaging';
import { generators, websocket, AdminMessageType, type AdminSessionExitAction } from '@playcastdotio/Messaging';
import type { PeerTypeHandler, SerializableMessage } from '../types';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import {
  hostInputDominanceState,
  hostMatchmakingState,
  machineIdState,
} from '../../../api/stores/shared'; // Accepted exception per user guidance
import { AdminPowerShellScriptManager } from '../managers/AdminPowerShellScriptManager';

export type AdminSessionDisconnectReason = 'admin_exit' | 'timeout' | 'unexpected';

export type AdminPeerHandlerConfig = {
  idleTimeout: number;
  idleWarning: number;
};

const DEFAULT_CONFIG: AdminPeerHandlerConfig = {
  idleTimeout: 300_000,
  idleWarning: 240_000,
};

const ADMIN_SESSION_EXIT_ACTIONS: AdminSessionExitAction[] = [
  'reboot',
  'restart_game',
  'keep_game',
  'exit_nonsole',
];

function parseAdminSessionExitAction(
  msg: { exitAction?: unknown; reboot?: unknown },
  fallback: AdminSessionExitAction = 'exit_nonsole',
): AdminSessionExitAction {
  if (
    typeof msg.exitAction === 'string'
    && ADMIN_SESSION_EXIT_ACTIONS.includes(msg.exitAction as AdminSessionExitAction)
  ) {
    return msg.exitAction as AdminSessionExitAction;
  }
  if (msg.reboot === true) {
    return 'reboot';
  }
  return fallback;
}

export interface ISessionPresenceUpdater {
  setAdminSessionActive(active: boolean): void;
  removeHostingStatus(): void;
}

export type AdminPeerHandlerDeps = {
  ipcService: IIpcService;
  realtimeService: IRealtimeService;
  appCoordinator: AppCoordinator;
  peerCoordinator: PeerCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionPresenceUpdater: ISessionPresenceUpdater;
};

export class AdminPeerHandler implements PeerTypeHandler {
  private isAdminSession = false;
  private adminPeerId: string | null = null;
  private adminDisconnectReason: AdminSessionDisconnectReason = 'unexpected';
  private adminRebootRequested = false;
  private adminExitAction: AdminSessionExitAction = 'exit_nonsole';
  private idleWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private idleKickTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly psScriptManager: AdminPowerShellScriptManager;

  constructor(
    private readonly deps: AdminPeerHandlerDeps,
    private readonly config: AdminPeerHandlerConfig = DEFAULT_CONFIG,
  ) {
    this.psScriptManager = new AdminPowerShellScriptManager({
      ipcService: deps.ipcService,
      appCoordinator: deps.appCoordinator,
      notifyActivity: () => this.resetIdleTimer(),
    });
  }

  // ========================================
  // PeerTypeHandler interface
  // ========================================

  public onConnected(peerId: string, _metadata: PeerMetadata): void {
    console.log('[AdminPeerHandler] Admin peer connected:', peerId);

    this.isAdminSession = true;
    this.adminPeerId = peerId;
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    this.adminExitAction = 'exit_nonsole';

    const hostId = this.deps.appCoordinator.getHostAuthId();

    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, { keyboard: false, mouse: false, gamepad: false }),
    );
    console.log('[AdminPeerHandler] Input filtering disabled for admin session');

    // Refresh display → shadowcam binding before WebRTC attaches (reduces solid/green placeholder frames).
    this.deps.ipcService.send(generators.camera.getSources());

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(true);
    console.log('[AdminPeerHandler] Matchmaking recalculated for admin session start');

    this.deps.sessionPresenceUpdater.setAdminSessionActive(true);

    this.startIdleTimer();
  }

  public onDisconnected(peerId: string): void {
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;

    console.log('[AdminPeerHandler] Admin peer disconnected:', peerId, 'reason:', this.adminDisconnectReason);

    const hostId = this.deps.appCoordinator.getHostAuthId();
    const exitAction = this.adminExitAction;
    const exitGuestMode = exitAction === 'reboot' || exitAction === 'exit_nonsole';
    const shouldReboot = exitAction === 'reboot';

    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, {
        keyboard: true,
        mouse: true,
        gamepad: true,
        exitGuestModeAfterAdminSession: exitGuestMode,
        rebootIfSafeAfterGuestModeExit: shouldReboot,
      }),
    );

    // Clear stale dominance so the next physical key/mouse edge triggers host hard-kick again (Playjector
    // reset alone did not always sync host UI state before InputThread.cpp fix; this covers older builds too).
    hostInputDominanceState.updateState({ keyboard: false, mouse: false });

    const guestAccount = hostMatchmakingState.currentState.guestAccount;
    if (guestAccount) {
      this.deps.ipcService.send(
        generators.process.processSetPolicy(
          hostId,
          true,
          false,
          true,
          true,
          60000,
          true,
          true,
          true,
          true,
          true,
        ),
      );
    }

    this.clearIdleTimer();

    if (exitAction === 'restart_game') {
      void this.deps.pavilionCoordinator.restartGameForAdminExit().catch((err) => {
        console.error('[AdminPeerHandler] restartGameForAdminExit failed:', err);
      });
    } else if (exitGuestMode) {
      // Destructive exits (reboot / exit Nonsole) tear the machine down. Force
      // Pavilion into a terminal non-matchmakeable state so it cannot re-enter
      // the pool in the window before PlaycastService completes the guest-mode
      // exit / reboot. This also stops the broadcast (removes hosting status).
      this.deps.pavilionCoordinator.enterTerminalExitState(exitAction);
    } else {
      // keep_game: stop the current stream but let the host re-enter matchmaking
      // so the next player can connect to the still-running game.
      this.deps.sessionPresenceUpdater.removeHostingStatus();
    }

    const machineId = machineIdState.currentState;
    this.deps.realtimeService.sendMessage(
      websocket.generators.admin(AdminMessageType.ADMIN_SESSION_END, {
        sessionId: peerId,
        machineId,
        reason: this.adminDisconnectReason,
      }),
    );

    this.isAdminSession = false;
    this.adminPeerId = null;
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    this.adminExitAction = 'exit_nonsole';
    this.psScriptManager.clearTransfers();

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(false);
    this.deps.sessionPresenceUpdater.setAdminSessionActive(false);
    console.log('[AdminPeerHandler] Matchmaking recalculated after admin session end');

    if (shouldReboot) {
      console.log(
        '[AdminPeerHandler] Reboot-after-admin requested via toggleFilters (PlaycastService exits guest mode first, then reboots if no user sessions remain)',
      );
    } else if (exitAction === 'restart_game') {
      console.log('[AdminPeerHandler] Admin exit restart_game — game relaunch requested, Nonsole Mode retained');
    } else if (exitAction === 'keep_game') {
      console.log('[AdminPeerHandler] Admin exit keep_game — game and Nonsole Mode retained');
    }
  }

  public onMessage(peerId: string, _message: SerializableMessage): void {
    if (this.adminPeerId === peerId) {
      this.resetIdleTimer();
    }
  }

  // ========================================
  // Admin control messages (from data channel via InputManager)
  // ========================================

  public handleControlMessage(peerId: string, body: unknown): void {
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;

    const msg = body as Record<string, unknown> | undefined;
    if (msg?.adminSessionExit === true) {
      console.log('[AdminPeerHandler] Received adminSessionExit from admin peer');
      this.adminDisconnectReason = 'admin_exit';
      this.adminExitAction = parseAdminSessionExitAction(msg, 'exit_nonsole');
      this.adminRebootRequested = this.adminExitAction === 'reboot';
      if (this.adminRebootRequested) {
        console.log('[AdminPeerHandler] Reboot requested with admin exit');
      }
      return;
    }

    if (msg?.powerShellScriptChunk === true) {
      this.psScriptManager.handleScriptChunk(msg);
      return;
    }

    if (msg?.launchElevatedProcess === true) {
      this.handleLaunchElevatedProcess(msg);
      return;
    }

    this.resetIdleTimer();
  }

  // ========================================
  // On-demand elevated process launch
  // ========================================

  private static readonly ALLOWED_EXECUTABLES: ReadonlySet<string> = new Set([
    'C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\powershell.exe',
    'C:\\Windows\\System32\\cmd.exe',
  ]);

  private handleLaunchElevatedProcess(msg: Record<string, unknown>): void {
    const path = typeof msg.path === 'string' ? msg.path : '';
    const args = typeof msg.arguments === 'string' ? msg.arguments : '';
    const cwd = typeof msg.workingDirectory === 'string' ? msg.workingDirectory : 'C:\\';

    if (!AdminPeerHandler.ALLOWED_EXECUTABLES.has(path)) {
      console.error('[AdminPeerHandler] Rejected elevated launch — path not in allowlist:', path);
      return;
    }

    const hostId = this.deps.appCoordinator.getHostAuthId();
    this.deps.ipcService.send(
      generators.system.launchElevatedProcess(hostId, path, args, cwd, undefined, { fromAdminSession: true }),
    );
    console.log('[AdminPeerHandler] On-demand elevated process launch requested:', path);
    this.resetIdleTimer();
  }

  // ========================================
  // Admin WebSocket messages (from realtime handler)
  // ========================================

  public handleAdminSessionMessage(message: { type: string; [key: string]: unknown }): void {
    if (message.type !== AdminMessageType.ADMIN_SESSION_END) return;
    if (!this.isAdminSession || !this.adminPeerId) return;

    const { reboot, exitAction } = message as { reboot?: boolean; exitAction?: AdminSessionExitAction; type: string };
    const parsedExitAction = parseAdminSessionExitAction({ exitAction, reboot }, 'exit_nonsole');
    console.log(`[AdminPeerHandler] Received ADMIN_SESSION_END from server (reboot=${!!reboot}, exitAction=${parsedExitAction})`);

    this.adminExitAction = parsedExitAction;
    if (parsedExitAction === 'reboot' || reboot) {
      this.adminRebootRequested = true;
    }

    if (this.adminDisconnectReason === 'unexpected') {
      this.adminDisconnectReason = 'admin_exit';
    }

    const kickReason = parsedExitAction === 'reboot' || reboot
      ? 'admin_session_end:reboot'
      : 'admin_session_end:server';
    this.deps.peerCoordinator.kickPeer(this.adminPeerId, kickReason);
  }

  // ========================================
  // Public getters
  // ========================================

  public getIsAdminSession(): boolean {
    return this.isAdminSession;
  }

  public getAdminPeerId(): string | null {
    return this.adminPeerId;
  }

  // ========================================
  // Idle timer
  // ========================================

  public resetIdleTimer(): void {
    if (this.isAdminSession && this.adminPeerId) {
      this.startIdleTimer();
    }
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();

    this.idleWarningTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle warning — 1 minute remaining');

      const remainingMs = this.config.idleTimeout - this.config.idleWarning;
      this.deps.realtimeService.sendMessage(
        websocket.generators.admin(AdminMessageType.ADMIN_SESSION_TIMEOUT_WARNING, {
          sessionId: this.adminPeerId,
          remainingSeconds: Math.round(remainingMs / 1000),
        }),
      );
    }, this.config.idleWarning);

    this.idleKickTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle timeout expired — kicking admin peer');
      this.adminDisconnectReason = 'timeout';
      this.deps.peerCoordinator.kickPeer(this.adminPeerId, 'kicked:admin_idle');
    }, this.config.idleTimeout);
  }

  private clearIdleTimer(): void {
    if (this.idleWarningTimer) {
      clearTimeout(this.idleWarningTimer);
      this.idleWarningTimer = null;
    }
    if (this.idleKickTimer) {
      clearTimeout(this.idleKickTimer);
      this.idleKickTimer = null;
    }
  }
}
